import type { DisciplineSlug } from "@jumpbean/shared";

export interface ActivityItem {
  id: string;
  type: "badge" | "streak" | "milestone" | "session";
  title: string;
  description?: string;
  timestamp: string;
  discipline?: DisciplineSlug;
  emoji?: string;
}

export interface ActivityTimelineProps {
  activities: ActivityItem[];
  maxItems?: number;
  emptyMessage?: string;
}

/**
 * Timeline component showing recent learning activity.
 * Displays badges earned, streaks, and milestones.
 */
export function ActivityTimeline({
  activities,
  maxItems = 5,
  emptyMessage = "No recent activity yet. Start playing!",
}: ActivityTimelineProps) {
  const displayedActivities = activities.slice(0, maxItems);

  // Discipline colors for badges
  const disciplineColors: Record<DisciplineSlug, string> = {
    math: "bg-gradient-to-br from-luna-light to-magic-300",
    spelling: "bg-gradient-to-br from-petal-light to-meadow-300",
    patterns: "bg-gradient-to-br from-fairy-200 to-prism-blue",
    typing: "bg-gradient-to-br from-whiskers-light to-sky-400",
    programming: "bg-gradient-to-br from-pixel-light to-pixel",
  };

  // Type-based icons
  const typeIcons: Record<ActivityItem["type"], string> = {
    badge: "🏅",
    streak: "🔥",
    milestone: "🎉",
    session: "📚",
  };

  // Format relative time
  function formatRelativeTime(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  if (displayedActivities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <span className="text-4xl mb-3" role="img" aria-hidden="true">
          🌟
        </span>
        <p className="font-body text-body-md text-neutral-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-gradient-to-b from-sunshine-200 via-magic-200 to-meadow-200 rounded-full" />

      {/* Activity items */}
      <div className="space-y-4">
        {displayedActivities.map((activity, index) => (
          <div
            key={activity.id}
            className="relative flex items-start gap-4 pl-2"
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          >
            {/* Timeline dot */}
            <div
              className={`
                relative z-10 flex-shrink-0 w-8 h-8 rounded-full
                flex items-center justify-center
                shadow-sm border-2 border-cloud
                ${activity.discipline ? disciplineColors[activity.discipline] : "bg-sunshine-300"}
              `}
            >
              <span className="text-sm" role="img" aria-hidden="true">
                {activity.emoji || typeIcons[activity.type]}
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pt-0.5">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-body font-semibold text-body-md text-neutral-800 truncate">
                  {activity.title}
                </p>
                <span className="flex-shrink-0 font-body text-xs text-neutral-400">
                  {formatRelativeTime(activity.timestamp)}
                </span>
              </div>
              {activity.description && (
                <p className="font-body text-sm text-neutral-500 mt-0.5 line-clamp-1">
                  {activity.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ActivityTimeline;
